import { LogoMainHorizontalWhite, ThemeAwareLogoMainHorizontal } from '@/components/brand';
import { ArrowLeft, ArrowUp, Instagram, Youtube } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import useScrollToTopVisibility from '@/hooks/use-scroll-to-top-visibility';
import type { ReactNode } from 'react';

type Props = {
    progress?: number;
    onBack?: () => void;
    backLabel?: string;
    mainClassName?: string;
    children: ReactNode;
};

export default function TestPageLayout({
    progress = 0,
    onBack,
    backLabel = 'Voltar',
    mainClassName,
    children,
}: Props) {
    const showScrollTop = useScrollToTopVisibility(300);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="relative flex min-h-screen flex-col bg-background">
            <div className="sticky top-0 z-50 w-full bg-card shadow-sm">
                <Progress value={progress} className="h-1.5 rounded-none [&>*]:!bg-info" />
                <header className={`bg-card ${onBack ? 'relative p-4' : 'py-6'}`}>
                    {onBack && (
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onBack}
                                className="text-muted-foreground hover:bg-brand-subtle hover:text-brand"
                            >
                                <ArrowLeft />
                                {backLabel}
                            </Button>
                        </div>
                    )}
                    <div className="container mx-auto flex max-w-3xl justify-center px-4">
                        <ThemeAwareLogoMainHorizontal />
                    </div>
                </header>
            </div>

            <main className={`container mx-auto flex-1 px-4 py-8 ${mainClassName || 'max-w-3xl'}`}>
                {children}
            </main>

            <footer className="mt-12 bg-primary py-12 text-primary-foreground dark:border-t dark:border-border dark:bg-card dark:text-card-foreground">
                <div className="container mx-auto flex max-w-3xl flex-col items-center justify-between gap-6 px-4 md:flex-row">
                    <div className="opacity-90 transition-opacity hover:opacity-100">
                        <LogoMainHorizontalWhite />
                        <p className="mt-2 text-sm text-primary-foreground/80 dark:text-muted-foreground">
                            &copy;&nbsp;{new Date().getFullYear()} LaudoPsi. Todos os direitos
                            reservados.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-primary-foreground/70 dark:text-muted-foreground md:items-end">
                        <a
                            href="https://www.instagram.com/laudopsi/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 transition-colors hover:text-primary-foreground dark:hover:text-foreground"
                        >
                            <Instagram className="size-7" />
                        </a>
                        <a
                            href="https://www.youtube.com/@LaudoPsi"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 transition-colors hover:text-primary-foreground dark:hover:text-foreground"
                        >
                            <Youtube className="size-7" />
                        </a>
                    </div>
                </div>
            </footer>

            {showScrollTop && (
                <Button
                    onClick={scrollToTop}
                    className="fixed bottom-8 right-8 z-50 size-12 rounded-full p-0 shadow-elevation-2 transition duration-200 animate-in zoom-in"
                    aria-label="Voltar ao topo"
                >
                    <ArrowUp className="size-6" />
                </Button>
            )}
        </div>
    );
}
